import { FC } from 'react';
import {
  PolarAngleAxis,
  PolarGrid,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { useThemeValue } from '@altermaind/web/ui';

type Props = {
  data: { subject: string; current: number; expected?: number; description?: string }[];
  chartHeight?: number;
};

type RadarTickPayload = {
  value: string;
};

export const createRadarTick = (
  data: { subject: string; current: number; description?: string }[],
) => {
  const RadarTick = (props: { x?: number; y?: number; payload?: RadarTickPayload }) => {
    const { x = 0, y = 0, payload } = props;
    const subject = payload?.value ?? '';
    const current = data.find(d => d.subject === subject)?.current ?? '';
    return (
      <text x={x} y={y} textAnchor='middle' fill='#8C919D' fontSize={11}>
        <tspan x={x} dy='0'>
          {subject}
        </tspan>
        <tspan x={x} dy='22' fontSize={20} fill='#CBD5E1'>
          {current}
        </tspan>
      </text>
    );
  };
  RadarTick.displayName = 'RadarTick';
  return RadarTick;
};

export const RadarChartComponent: FC<Props> = ({ data, chartHeight = 400 }) => {
  const accentColor = useThemeValue('colors', 'primary');

  return (
    <ResponsiveContainer width='100%' height={chartHeight}>
      <RadarChart
        cx='50%'
        cy='50%'
        outerRadius='80%'
        data={data.sort((a, b) => a.subject.localeCompare(b.subject))}
      >
        <PolarGrid stroke='#575757' polarRadius={[]} />
        <PolarAngleAxis dataKey='subject' tick={createRadarTick(data)} />
        <Radar name='Current' dataKey='current' fill='url(#gradient)' fillOpacity={0.7} />
        <Radar name='Expected' dataKey='expected' fill='url(#gradient)' fillOpacity={0} />
        <defs>
          <linearGradient id='gradient' x1='0' y1='0' x2='0' y2='1'>
            <stop offset='0%' stopColor={accentColor} stopOpacity={0.8} />
            <stop offset='100%' stopColor={accentColor} stopOpacity={0.4} />
          </linearGradient>
        </defs>
        <Tooltip
          // eslint-disable-next-line react/no-unstable-nested-components
          content={({ active, payload }) => {
            if (!active || !payload || payload.length === 0) return null;
            const { subject } = payload[0].payload;
            const item = data.find(d => d.subject === subject);
            return (
              <div
                style={{
                  backgroundColor: '#fff',
                  color: '#000',
                  borderRadius: '8px',
                  padding: '6px 10px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  fontSize: '14px',
                  border: 'none',
                }}
              >
                {item?.description ?? ''}
              </div>
            );
          }}
          contentStyle={{
            backgroundColor: '#fff',
            color: '#000',
            borderRadius: '8px',
            padding: '6px 10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
            fontSize: '14px',
            border: 'none',
          }}
        />
      </RadarChart>
    </ResponsiveContainer>
  );
};
